import { Location } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { resetStores, selectPersistStateInit } from '@datorama/akita';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BroadcastEventService } from '@y42/browser/broadcast-event';
import { environment, ENV_NAME } from '@y42/environment';
import { SentryService } from '@y42/error-handling';
import { IntercomControlService } from '@y42/intercom';
import { UserTrackingLegacyService } from '@y42/user-tracking-legacy';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { catchError, firstValueFrom, from, NEVER, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { AuthChangeBroadcastEvent } from '../models/auth-broadcast.model';
import { SsoSessionModel } from '../models/sso-session.model';
import { AuthQuery } from '../state/auth.query';
import { AuthStore } from '../state/auth.store';
import { IdentityClaims } from './sso/identity-claims.model';
import { SsoSessionService } from './sso/sso-session.service';
import { SsoStorageService } from './sso/sso-storage.service';
import { SessionDurationTrackingEventModel } from './tracking/session-duration-tracking-event.model';
import { SignedInTrackingEvent } from './tracking/signed-in-tracking-event.model';
import { SignedOutTrackingEvent } from './tracking/signed-out-tracking-event.model';
import { TwoFactorService } from './two-factor.service';

// Useful OpenID and OAuth documentation:
// https://janrain-education-center.knowledgeowl.com/home/hosted-login

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    private readonly authQuery: AuthQuery,
    private readonly authStore: AuthStore,
    private readonly oAuthService: OAuthService,
    private readonly userTracking: UserTrackingLegacyService,
    private readonly sentry: SentryService,
    private readonly broadcastEventService: BroadcastEventService,
    private readonly ssoStorageService: SsoStorageService,
    private readonly ssoSessionService: SsoSessionService,
    private readonly twoFactorService: TwoFactorService,
    private readonly location: Location,
    private readonly intercom: IntercomControlService,
    @Inject(ENV_NAME) private readonly envName: string,
  ) {}

  static REFRESH_TOKEN_BEFORE_EXPIRY_MS = 30_000;

  private readonly signOut$$ = new Subject<void>();
  readonly signOut$ = this.signOut$$.pipe(untilDestroyed(this));

  async initSignInFlow(): Promise<void> {
    console.debug('[AuthService]: starting authentication flow');

    this.ssoStorageService.persistSsoAuth();

    // Blocking promise. When the user is not logged-in, the promise does not resolve and the user is redirected to the login page
    await this.configureOAuthClientAndTryLogin();

    this.authStore.setToken(this.oAuthService.getAccessToken());

    await this.setSsoSession();

    this.fetchTwoFactorState();

    this.onRefreshAccessTokenSyncWithStore();
    this.refreshAccessTokenOnTokenExpiry().pipe(untilDestroyed(this)).subscribe();

    console.debug('[AuthService]: user successfully authenticated');
  }

  async signIn(options?: { saveCurrentUrl?: boolean }): Promise<void> {
    console.debug('[AuthService]: starting login', options);

    if (this.authQuery.getAccessToken()) {
      this.authStore.clearToken();
    }

    if (options?.saveCurrentUrl) {
      const redirectUrl = this.location.path();
      console.debug(`[AuthService]: redirect url after login '${redirectUrl || '/'}'`);
      this.authStore.setRedirectUrlAfterLogin(redirectUrl);
    }

    this.oAuthService.initCodeFlow('', this.ssoStorageService.getSsoAuthFromStorage());

    // Never-ending promise
    // The authentication flow will redirect to the login page
    return new Promise(() => null);
  }

  async initResetPasswordFlow(resetToken: string): Promise<void> {
    await this.configureOAuthClient();

    this.oAuthService.initCodeFlow('', { resetToken, type: 'forgotten' });

    // Never-ending promise
    // The authentication flow will redirect to the login page
    return new Promise(() => null);
  }

  async signOut(): Promise<void> {
    await Promise.all([this.userTracking.track(new SignedOutTrackingEvent()), this.stopTrackingSession()]);
    this.sentry.clearUser();
    this.intercom.closeIntercom();

    await this.oAuthService.revokeTokenAndLogout();

    resetStores();

    localStorage.clear();
    sessionStorage.clear();
  }

  async stopTrackingSession(): Promise<void> {
    const ageInSeconds = (Date.now() - parseInt(sessionStorage.getItem('sessionStartedAt') || '0')) / 1000;
    await this.userTracking.track(new SessionDurationTrackingEventModel({ sessionDuration: ageInSeconds?.toString() }));
    sessionStorage.removeItem('sessionStartedAt');
  }

  getIdentityClaims(): IdentityClaims {
    const identityClaims = this.oAuthService.getIdentityClaims() as IdentityClaims | null;

    if (!identityClaims) {
      throw new Error('[AuthService]: null identity claims! clean the localStorage and try again');
    }

    return identityClaims;
  }

  startTrackingSession(): void {
    this.userTracking.track(new SignedInTrackingEvent());

    if (!sessionStorage.getItem('sessionStartedAt')) {
      sessionStorage.setItem('sessionStartedAt', Date.now().toString());
    }
  }

  /**
   * Returns the access token and guarantees that it is fresh.
   *
   * If the token is about to expire, it will first refresh the token
   * before returning with the new one.
   *
   * If the token is currently being refreshed it waits with the emission until the refresh has finalized.
   */
  selectToken(): Observable<string> {
    // there is a brief <1 second period
    // where we can be logged out / have no access token
    // and some open subscriptions still making HTTP requests => resulting in errors
    // so we just don't emit, since 1 second later we get redirected to the login screen anyway
    if (!this.authQuery.isLoggedIn()) {
      console.debug('[AuthService.selectToken]: user is logged out');
      return NEVER;
    }

    if (this.authQuery.getTokenRefreshing()) {
      return this.authQuery.tokenRefreshing$.pipe(
        filter((refreshing) => !refreshing),
        switchMap(() => this.authQuery.token$),
      );
    }

    const now = Date.now();
    const tokenExpiresIn = this.oAuthService.getAccessTokenExpiration() - now;
    const isTokenAboutToExpire = tokenExpiresIn < AuthService.REFRESH_TOKEN_BEFORE_EXPIRY_MS;

    if (isTokenAboutToExpire) {
      return this.refreshToken();
    }

    return this.authQuery.token$;
  }

  private refreshToken(): Observable<string> {
    console.debug('[AuthService]: refreshing token');
    this.authStore.setTokenRefreshing();

    return from(this.oAuthService.refreshToken()).pipe(
      catchError(async (err: unknown) => {
        console.error('[AuthService]: token refresh failed', err);

        localStorage.clear();
        sessionStorage.clear();

        await this.signIn({ saveCurrentUrl: true });

        // Non-reachable code
        throw new Error('[AuthService]: should have been redirected to the login page');
      }),
      tap(() => {
        console.debug('[AuthService]: token refreshed');
        const token = this.oAuthService.getAccessToken();
        this.authStore.setToken(token);
        this.broadcastEventService.broadcast(new AuthChangeBroadcastEvent({ token }));
      }),
      switchMap(() => this.authQuery.token$),
    );
  }

  private async setSsoSession(): Promise<void> {
    const userSsoSession = await this.getSsoSession();
    this.authStore.setSsoSession(userSsoSession);

    this.ssoStorageService.clearSsoAuth();
  }

  private fetchTwoFactorState() {
    // Since the auth store is persisted in the local storage and the fetch is not awaited, the application
    // starts with the old QR code generated when the user doesn't have enabled the 2fa, that's why it needs
    // te be cleaned
    this.authStore.clearTwoFactorState();

    const userId = this.getAuthenticatedUserId();

    this.twoFactorService.getState(userId).subscribe();
  }

  private async configureOAuthClientAndTryLogin(): Promise<void> {
    await firstValueFrom(
      selectPersistStateInit().pipe(
        switchMap(() => from(this.configureOAuthClient())),
        switchMap(async () => {
          if (!this.oAuthService.hasValidAccessToken()) {
            console.debug('[AuthService]: invalid token');

            await this.signIn({ saveCurrentUrl: true });
          }
          console.debug('[AuthService]: user already has a valid token');

          const ssoAuth = this.ssoStorageService.getSsoAuthFromQueryParams();
          if (ssoAuth.impersonation_id) {
            console.debug('[AuthService]: active impersonation singin');
            await this.signIn({ saveCurrentUrl: true });
          }
        }),
      ),
    );
  }

  private async configureOAuthClient(): Promise<void> {
    const redirectBaseUrl = window.location.origin;

    this.oAuthService.configure({
      issuer: environment.auth.issuer,
      redirectUri: `${redirectBaseUrl}/login`,
      clientId: environment.auth.clientId,
      scope: 'organiser offline_access',
      disableAtHashCheck: false,
      responseType: 'code',
      logoutUrl: `${redirectBaseUrl}/logout`,
      showDebugInformation: this.envName !== 'prod',
      // we refresh the token manually
      useSilentRefresh: false,
    });

    this.oAuthService.events
      .pipe(
        filter((event) => event instanceof OAuthErrorEvent),
        filter((msg) => msg.type === 'code_error'),
        switchMap(async (event) => {
          console.error(`[AuthService]: login failed`, (event as OAuthErrorEvent).params);
          await this.signIn();

          // Non-reachable code
          throw new Error('[AuthService]: should have been redirected to the login page');
        }),
        untilDestroyed(this),
      )
      .subscribe();

    const isConnectedToOauth = await this.oAuthService.loadDiscoveryDocumentAndTryLogin();
    if (!isConnectedToOauth) {
      throw new Error('[AuthService]: failed to load discovery document');
    }
  }

  private onRefreshAccessTokenSyncWithStore(): void {
    this.broadcastEventService
      .listen(AuthChangeBroadcastEvent)
      .pipe(
        map((payload) => payload.token),
        switchMap((token) => this.otherTabRefreshedAccessToken(token)),
        untilDestroyed(this),
      )
      .subscribe();
  }

  /**
   * Make sure to always keep fresh token,
   * as e.g. Pusher depends on it since it doesn't make a HTTP request
   */
  private refreshAccessTokenOnTokenExpiry(): Observable<string> {
    return this.oAuthService.events.pipe(
      filter((event) => event.type === 'token_expires'),
      switchMap(() => this.refreshToken()),
    );
  }

  private async otherTabRefreshedAccessToken(accessToken: string | undefined): Promise<void> {
    if (accessToken) {
      this.authStore.setToken(accessToken);
    } else {
      await this.signOut();
    }
  }

  private getSsoSession(): Promise<SsoSessionModel | null> {
    const userId = this.getAuthenticatedUserId();
    return firstValueFrom(
      this.ssoSessionService.getSsoSession(userId).pipe(
        catchError((err: unknown) => {
          console.error(`[AuthService]: error while fetching sso session`, err);
          return of(null);
        }),
      ),
    );
  }

  private getAuthenticatedUserId(): number {
    const identityClaims = this.getIdentityClaims();
    return Number(identityClaims.sub);
  }
}
